import { useQuery } from "@tanstack/react-query";
import { Film, Users, Award } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import PtfButton from "@/components/PtfButton";
import { Reveal } from "@/components/motion/Reveal";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/content";
import { ExperienceCard } from "@/pages/Exploration";

const STEPS = [
  {
    num: "01",
    icon: Users,
    title: "You join the expedition",
    text: "A small group travels long-format through one landscape: its villages, its trails, its people. You are a participant, not an audience.",
  },
  {
    num: "02",
    icon: Film,
    title: "The journey is filmed",
    text: "A documentary crew travels with the group. The film is not about the travellers. It is about the place, told through the journey.",
  },
  {
    num: "03",
    icon: Award,
    title: "The film travels further",
    text: "Finished films are submitted to film festivals in India and abroad, carrying the story of the region, and the case for protecting it, to rooms we could never reach on foot.",
  },
];

export default function Flagship() {
  const { data: journeys = [] } = useQuery({
    queryKey: ["journeys"],
    queryFn: async () => (await api.get("/journeys")).data,
  });
  const flagships = journeys.filter((j) => j.is_flagship);

  return (
    <>
      <Seo title="Flagship Expeditions" path="/flagship" description="Planet Trotter Foundation's flagship documentary-led expeditions: travellers join, the journey is filmed, and the film carries the story of the place to festivals in India and abroad." />
      <PageHero
        eyebrow="Flagship Programme"
        titleLines={["Expeditions that", "become films."]}
        intro="Our flagship format joins two crafts: long-format expedition travel and documentary filmmaking. The travellers fund the journey. The journey becomes the film. The film becomes the argument."
        image={IMAGES.bigIdea}
      />

      <section className="bg-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <p className="text-[0.7rem] uppercase tracking-[0.28em] text-ptf-primary/60 mb-12 flex items-center gap-3">
            <span className="h-px w-8 bg-ptf-primary/40 inline-block" /> How it works
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.1}>
                <div data-testid={`flagship-step-${s.num}`} className="border border-ptf-primary/12 bg-white p-8 h-full">
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-display text-5xl text-ptf-primary/15">{s.num}</span>
                    <span className="grid place-items-center h-11 w-11 rounded-full bg-ptf-primary/5"><s.icon className="h-5 w-5 text-ptf-secondary" /></span>
                  </div>
                  <h3 className="font-display uppercase text-2xl tracking-tight text-ptf-primary leading-none">{s.title}</h3>
                  <p className="mt-4 text-sm text-ptf-text/70 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ptf-primary text-ptf-bg py-20 md:py-28 grain">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <h2 className="font-display font-extrabold uppercase text-4xl md:text-6xl tracking-tight leading-[0.95]">
              Why film the journey?
            </h2>
            <p className="mt-8 text-ptf-bg/75 leading-relaxed max-w-2xl">
              A trek changes the twelve people who walk it. A film about that trek can
              change how thousands see the valley it crossed. Documentary storytelling
              extends the reach of every expedition far beyond its own footprints, and
              gives the communities who host us a stage that lasts longer than a season.
            </p>
            <p className="mt-5 text-ptf-bg/75 leading-relaxed max-w-2xl">
              Recognition at festivals is not the goal in itself. It is a lever: for
              partnerships, for funding, and for the case that these landscapes and the
              people who keep them are worth protecting.
            </p>
          </div>
          <div className="lg:col-span-5 lg:justify-self-end lg:self-end">
            <div className="flex flex-col sm:flex-row gap-3">
              <PtfButton to="/expeditions" variant="accent" data-testid="flagship-to-travel">Browse the Expeditions</PtfButton>
              <PtfButton to="/partnerships" variant="outlineLight">Partner on a Film</PtfButton>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-ptf-bg py-20 md:py-28">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <h2 className="font-display font-extrabold uppercase text-3xl md:text-5xl tracking-tight text-ptf-primary leading-[0.95] mb-12">
            Flagship expeditions
          </h2>
          {flagships.length === 0 ? (
            <Reveal>
              <div data-testid="flagship-empty" className="border border-dashed border-ptf-primary/25 py-20 px-8 text-center">
                <span className="grid place-items-center h-16 w-16 rounded-full bg-ptf-primary/5 mx-auto mb-6">
                  <Film className="h-7 w-7 text-ptf-secondary" />
                </span>
                <h3 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">The first flagship is in planning.</h3>
                <p className="mt-4 max-w-lg mx-auto text-ptf-text/70 leading-relaxed">
                  Route, crew and dates are being worked out. It will be published under
                  Responsible Exploration, and here, the moment it is ready.
                </p>
              </div>
            </Reveal>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {flagships.map((j, i) => (
                <ExperienceCard key={j.id} j={j} delay={(i % 3) * 0.06} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
